// @ts-nocheck
import React, { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';

interface SortOption {
	columnName: string;
	displayName: string;
}

interface SkuSelectionBoxProps {
	skuList: any[];
	skuIndex: number;
	onNavigate: (direction: number) => void;
	onSelectIndex: (index: number) => void;
	loading: boolean;
	availInfo?: {
		initialAvail: number;
		currentAvail: number;
		orderedQty: number;
		remainQty: number;
	};
	/** Available SKU-level columns to sort by (e.g., Genus, Brand, Size) */
	sortOptions?: SortOption[];
	/** Currently active sort field (empty = default/discovery order) */
	sortField?: string;
	/** Callback when user changes the sort field */
	onSortChange?: (field: string) => void;
}

const SkuSelectionBox: React.FC<SkuSelectionBoxProps> = ({
	skuList,
	skuIndex,
	onNavigate,
	onSelectIndex,
	loading,
	availInfo,
	sortOptions = [],
	sortField = '',
	onSortChange,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const [skuSearch, setSkuSearch] = useState('');
	const [dropOpen, setDropOpen] = useState(false);
	const dropRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const currentSku = skuList[skuIndex] || null;

	const fieldNames = useMemo(() => {
		if (!currentSku) return {};
		const keys = Object.keys(currentSku);
		return {
			sku: keys.find((k) => /^sku$/i.test(k)) || keys.find((k) => /^skuid$/i.test(k)) || 'SKU',
			desc:
				keys.find((k) => /desc/i.test(k) && /sku/i.test(k)) ||
				keys.find((k) => /desc/i.test(k)) ||
				keys.find((k) => /name/i.test(k)),
			tableType: keys.find((k) => /tabletype/i.test(k)) || keys.find((k) => /table.*type/i.test(k)),
		};
	}, [currentSku]);

	const skuId = currentSku?.[fieldNames.sku] || '';
	const availQty = availInfo?.initialAvail ?? 0;
	const orderedQty = availInfo?.orderedQty ?? 0;
	const remainQty = availInfo?.remainQty ?? 0;
	const fmtNum = (n: number) => (n != null ? n.toLocaleString() : '0');

	// Build searchable list — include sort group value for section headers
	const skuOptions = useMemo(() => {
		return skuList.map((s, i) => {
			const id = s[fieldNames.sku] || s.SKU || s.SKUID || '';
			const desc = fieldNames.desc ? s[fieldNames.desc] || '' : '';
			const label = desc ? `${id} — ${desc}` : id || `SKU ${i + 1}`;
			const groupVal = sortField ? String(s[sortField] || '(none)') : '';
			return { idx: i, id, label, groupVal };
		});
	}, [skuList, fieldNames, sortField]);

	const filteredOptions = useMemo(() => {
		if (!skuSearch) return skuOptions;
		const s = skuSearch.toLowerCase();
		return skuOptions.filter((o) => o.label.toLowerCase().includes(s) || o.groupVal.toLowerCase().includes(s));
	}, [skuOptions, skuSearch]);

	// Close dropdown on outside click
	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	useEffect(() => {
		if (dropOpen && searchRef.current) searchRef.current.focus();
	}, [dropOpen]);

	const selectSku = useCallback(
		(idx: number) => {
			onSelectIndex(idx);
			setDropOpen(false);
			setSkuSearch('');
		},
		[onSelectIndex]
	);

	const currentLabel = skuOptions[skuIndex]?.label || '—';
	const currentGroup = skuOptions[skuIndex]?.groupVal || '';

	// Get display name for active sort field
	const sortDisplayName = useMemo(() => {
		if (!sortField) return '';
		return sortOptions.find((o) => o.columnName === sortField)?.displayName || sortField;
	}, [sortField, sortOptions]);

	return (
		<div className="obs-filter-box obs-sku-box">
			<div className="obs-filter-box__header" style={{ color: '#7b1fa2' }} onClick={() => setIsOpen(!isOpen)}>
				<span className="obs-filter-box__header-left">
					<span className={`obs-filter-box__chevron ${isOpen ? 'obs-filter-box__chevron--open' : ''}`}>
						▶
					</span>
					SKU Selection
				</span>
				<span className="obs-filter-box__count">
					{skuList.length > 0 ? `${skuIndex + 1}/${skuList.length}` : '—'}
				</span>
			</div>

			{isOpen && (
				<div className="obs-filter-box__body">
					{/* Navigator */}
					<div className="obs-sku-box__nav">
						<button
							className="obs-sku-box__nav-btn"
							onClick={() => onNavigate(-1)}
							disabled={skuIndex <= 0 || loading}
						>
							‹
						</button>
						<div className="obs-sku-box__nav-counter">
							{loading
								? 'Loading SKUs...'
								: skuList.length > 0
									? `SKU ${skuIndex + 1} of ${skuList.length}`
									: 'No SKUs'}
						</div>
						<button
							className="obs-sku-box__nav-btn"
							onClick={() => onNavigate(1)}
							disabled={skuIndex >= skuList.length - 1 || loading}
						>
							›
						</button>
						<button
							className="obs-sku-box__next-btn"
							onClick={() => onNavigate(1)}
							disabled={skuIndex >= skuList.length - 1 || loading}
						>
							Next SKU
						</button>
					</div>

					{/* Sort by attribute */}
					{sortOptions.length > 0 && (
						<div className="obs-sku-box__sort">
							<label className="obs-sku-box__sort-label">Sort by</label>
							<select
								className="obs-sku-box__sort-select"
								value={sortField}
								onChange={(e) => onSortChange?.(e.target.value)}
							>
								<option value="">Default order</option>
								{sortOptions.map((opt) => (
									<option key={opt.columnName} value={opt.columnName}>
										{opt.displayName}
									</option>
								))}
							</select>
						</div>
					)}

					{/* Searchable SKU dropdown */}
					<div className="obs-ss" ref={dropRef}>
						<div className="obs-ss__trigger" onClick={() => setDropOpen(!dropOpen)}>
							<span className="obs-ss__val">{currentLabel}</span>
							{sortField && currentGroup && (
								<span className="obs-ss__sort-badge" title={`${sortDisplayName}: ${currentGroup}`}>
									{currentGroup}
								</span>
							)}
							<span className="obs-ss__arrow">▾</span>
						</div>
						{dropOpen && (
							<div className="obs-ss__drop">
								<input
									ref={searchRef}
									className="obs-ss__search"
									type="text"
									placeholder={
										sortField
											? 'Search SKU, description, or group...'
											: 'Search SKU or description...'
									}
									value={skuSearch}
									onChange={(e) => setSkuSearch(e.target.value)}
									onClick={(e) => e.stopPropagation()}
								/>
								<div className="obs-ss__list" style={{ maxHeight: 260 }}>
									{filteredOptions.map((o, i) => {
										// Show group header when sort field is active and group changes
										const showGroupHeader =
											sortField &&
											!skuSearch &&
											(i === 0 || o.groupVal !== filteredOptions[i - 1]?.groupVal);
										return (
											<React.Fragment key={o.idx}>
												{showGroupHeader && (
													<div className="obs-ss__group-header">{o.groupVal}</div>
												)}
												<div
													className={`obs-ss__opt ${o.idx === skuIndex ? 'obs-ss__opt--sel' : ''}`}
													onClick={() => selectSku(o.idx)}
												>
													{o.label}
												</div>
											</React.Fragment>
										);
									})}
									{filteredOptions.length === 0 && (
										<div className="obs-ss__empty">No matching SKUs</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Availability metrics */}
					<div className="obs-sku-box__metrics">
						<div className={`obs-sku-box__metric ${availQty === 0 ? 'obs-sku-box__metric--alert' : ''}`}>
							<div className="obs-sku-box__metric-label">SKU Avail</div>
							<div className="obs-sku-box__metric-value">{fmtNum(availQty)}</div>
							<div className="obs-sku-box__metric-unit">ea</div>
						</div>
						<div className="obs-sku-box__metric">
							<div className="obs-sku-box__metric-label">SKU Ord</div>
							<div className="obs-sku-box__metric-value">{fmtNum(orderedQty)}</div>
							<div className="obs-sku-box__metric-unit">ea</div>
						</div>
						<div className={`obs-sku-box__metric ${remainQty < 0 ? 'obs-sku-box__metric--alert' : ''}`}>
							<div className="obs-sku-box__metric-label">SKU Remain</div>
							<div className="obs-sku-box__metric-value">{fmtNum(remainQty)}</div>
							<div className="obs-sku-box__metric-unit">ea</div>
						</div>
					</div>
					</div>
			)}
		</div>
	);
};

/**
 * Custom comparison — skip re-render when SKU list content, index, and availability haven't changed.
 * Avoids deep-equal on large skuList by comparing length + first/last SKU ids.
 */
function arePropsEqual(prev: SkuSelectionBoxProps, next: SkuSelectionBoxProps): boolean {
	if (prev.skuIndex !== next.skuIndex) return false;
	if (prev.loading !== next.loading) return false;
	if (prev.sortField !== next.sortField) return false;
	if (prev.onNavigate !== next.onNavigate) return false;
	if (prev.onSelectIndex !== next.onSelectIndex) return false;
	if (prev.onSortChange !== next.onSortChange) return false;

	// Shallow-compare availInfo fields
	const pa = prev.availInfo, na = next.availInfo;
	if (pa?.initialAvail !== na?.initialAvail || pa?.orderedQty !== na?.orderedQty
		|| pa?.remainQty !== na?.remainQty) return false;

	// Fast skuList comparison: length + first/last item reference
	const pl = prev.skuList, nl = next.skuList;
	if (pl.length !== nl.length) return false;
	if (pl.length > 0 && (pl[0] !== nl[0] || pl[pl.length - 1] !== nl[nl.length - 1])) return false;

	// sortOptions rarely changes — compare by length
	if ((prev.sortOptions?.length || 0) !== (next.sortOptions?.length || 0)) return false;

	return true;
}

export default memo(SkuSelectionBox, arePropsEqual);
